import { useEffect, useRef, useState } from 'react';
import { useConsent } from '../context/ConsentContext';

const API = process.env.REACT_APP_BACKEND_URL;
const FIRED_KEY = 'smartvolt_fired_conversions';

/**
 * Übermittelt nach einem erfolgreichen Digistore24-Kauf den `purchase`-Event
 * an Google Ads, Google Analytics 4 und – falls aktiviert – Meta Pixel,
 * LinkedIn und TikTok.
 *
 * Voraussetzungen:
 *   1. URL enthält `order_id` und `email` (Digistore24 Thank-You-Placeholder)
 *   2. Nutzer hat „Marketing"-Cookies akzeptiert (sonst werden nur essentielle
 *      Skripte geladen und der Event wird übersprungen)
 *   3. Admin hat im Cockpit eine gültige Conversion-ID hinterlegt
 *
 * Idempotenz: jede `order_id` wird nur EINMAL gefeuert (gespeichert in
 * localStorage), damit ein Reload der Danke-Seite Google Ads nicht doppelt
 * belastet.
 */

const readFiredSet = () => {
  try {
    const raw = localStorage.getItem(FIRED_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
};

const markFired = (orderId) => {
  try {
    const set = readFiredSet();
    set.add(orderId);
    // Limit auf die letzten 200 Bestellungen, damit der Storage nicht überläuft
    const list = Array.from(set).slice(-200);
    localStorage.setItem(FIRED_KEY, JSON.stringify(list));
  } catch { /* ignore */ }
};

const useConversionTracker = ({ orderId, email }) => {
  const { config, activeTrackers, categories } = useConsent();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | loading | fired | skipped | error
  const [error, setError] = useState(null);
  const firedRef = useRef(false);

  // Schritt 1: Conversion-Daten vom Backend abrufen (Betrag + Währung)
  useEffect(() => {
    if (!orderId || !email) {
      setStatus('skipped');
      return;
    }
    let cancelled = false;
    setStatus('loading');
    fetch(`${API}/api/conversion/order/${encodeURIComponent(orderId)}?email=${encodeURIComponent(email)}`)
      .then(async (r) => {
        const json = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(json.detail || `HTTP ${r.status}`);
        if (cancelled) return;
        setData(json);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Konnte Conversion-Daten nicht laden');
          setStatus('error');
        }
      });
    return () => { cancelled = true; };
  }, [orderId, email]);

  // Schritt 2: sobald Daten + Tracker-Config + Consent verfügbar → Events feuern
  useEffect(() => {
    if (!data || firedRef.current) return;
    if (!config) return;

    const alreadyFired = readFiredSet().has(orderId);
    if (alreadyFired) {
      firedRef.current = true;
      setStatus('fired');
      return;
    }

    // Wir feuern erst, wenn klar ist, was erlaubt ist.
    // Ohne Marketing-Consent: nur den GA4-Purchase (Statistik) feuern.
    const anyTrackerActive = Object.values(activeTrackers || {}).some(Boolean);
    if (!anyTrackerActive) {
      setStatus('skipped');
      return;
    }

    const value = Number(data.amount) || 0;
    const currency = (data.currency || 'EUR').toUpperCase();
    const transactionId = data.order_id;
    const items = [{
      item_id: data.product_id || 'smartvolt-academy',
      item_name: data.product_name || 'Smartvolt Academy Abo',
      price: value,
      quantity: Number(data.quantity || 1) }];

    // --- Google Ads Conversion ---------------------------------------------
    if (activeTrackers.googleAds && window.gtag && config.googleAds?.conversionId) {
      const id = config.googleAds.conversionId;
      const label = config.googleAds.conversionLabel;
      const sendTo = label ? `${id}/${label}` : id;
      try {
        window.gtag('event', 'conversion', {
          send_to: sendTo,
          value,
          currency,
          transaction_id: transactionId });
      } catch (e) { /* swallow – tracker fehler darf User nicht blockieren */ }
    }

    // --- Google Analytics 4 Purchase --------------------------------------
    if (activeTrackers.googleAnalytics && window.gtag) {
      try {
        window.gtag('event', 'purchase', {
          transaction_id: transactionId,
          value,
          currency,
          items });
      } catch (e) { /* swallow */ }
    }

    // --- Meta Pixel Purchase ----------------------------------------------
    if (activeTrackers.metaPixel && typeof window.fbq === 'function') {
      try {
        window.fbq('track', 'Purchase', {
          value,
          currency,
          content_ids: [data.product_id || 'smartvolt-academy'],
          content_type: 'product' }, { eventID: transactionId });
      } catch (e) { /* swallow */ }
    }

    // --- LinkedIn Conversion (optional) -----------------------------------
    if (activeTrackers.linkedInInsight && typeof window.lintrk === 'function') {
      try {
        window.lintrk('track', { conversion_id: transactionId });
      } catch (e) { /* swallow */ }
    }

    // --- TikTok CompletePayment -------------------------------------------
    if (activeTrackers.tiktokPixel && window.ttq?.track) {
      try {
        window.ttq.track('CompletePayment', {
          value,
          currency,
          content_id: data.product_id || 'smartvolt-academy' });
      } catch (e) { /* swallow */ }
    }

    markFired(orderId);
    firedRef.current = true;
    setStatus('fired');
  }, [data, config, activeTrackers, categories, orderId]);

  return { data, status, error };
};

export default useConversionTracker;
